import { useSnapshot } from "valtio";
import {
  PerAreaPriceModes,
  PerUnitPriceModes,
  PriceValuationType,
  ZooplaPricePerAreaModes,
  ZooplaPricePerUnitModes,
} from "react-migration/domains/comparables/typings/PriceModes";
import { EnrichedComparableDTO } from "react-migration/lib/typings/EnrichedComparables";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { Currency, formatToCurrency } from "react-migration/lib/util/numberFormat";
import { Locale } from "src/js/locales";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { MeasurementSystem } from "src/js/stores/user/store";
import { ftsqToMsq, msqToFtsq } from "src/js/util/units_and_constants";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { comparablesStore } from "src/js/stores/comparables";

export interface EnhancedTransaction extends EnrichedComparableDTO {
  formattedPrice: string;
  formattedPerAreaPrice: string | null;
  isRent: boolean;
  valuationType?: PriceValuationType;
}

export const pricePerUnitArea = ({
  price,
  area,
  unitPreference,
}: {
  price?: number;
  area?: number;
  unitPreference: MeasurementSystem;
}) => {
  if (!area || !price) {
    return 0;
  }

  return unitPreference === MeasurementSystem.IMPERIAL ? price / msqToFtsq(area) : price / area;
};

const calculateComparablePrice = (
  transaction: EnrichedComparableDTO,
  locale: Locale,
  currency: Currency,
  displayMode: string,
  hasFullAccess: boolean,
  unitPreference: MeasurementSystem,
  t: (key: string, params?: Record<string, unknown>) => string
): {
  formattedPrice: string;
  formattedPerAreaPrice: string | null;
  isRent: boolean;
  valuationType?: PriceValuationType;
} => {
  const attributes = transaction.zooplaEnrichment?.attributes;
  const totalFloorArea = transaction.total_floor_area;

  let zoopla = transaction.zooplaEnrichment?.valuation;
  let floorArea = totalFloorArea; // in sqm

  if (hasFullAccess && attributes?.floorArea) {
    floorArea = ftsqToMsq(attributes.floorArea); // convert sqft to sqm
  }

  if (!hasFullAccess) {
    zoopla = undefined;
  }

  let unitValue: number | null = null;
  let isRent = false;
  let valuationType: PriceValuationType | undefined;

  if (!hasFullAccess) {
    switch (displayMode) {
      case PerUnitPriceModes.PRICE:
      case PerAreaPriceModes.PPSM:
        unitValue = transaction.price;
        valuationType = PriceValuationType.SALE;
        break;
      case PerUnitPriceModes.EP:
      case PerAreaPriceModes.EPPSM:
        unitValue = transaction.est_price;
        valuationType = PriceValuationType.VALUATION;
        break;
      default:
        unitValue = null;
    }
  } else {
    switch (displayMode) {
      // Handle HMLR price modes even when Zoopla access is available
      case PerUnitPriceModes.PRICE:
      case PerAreaPriceModes.PPSM:
        unitValue = transaction.price;
        valuationType = PriceValuationType.SALE;
        break;
      case PerUnitPriceModes.EP:
      case PerAreaPriceModes.EPPSM:
        unitValue = transaction.est_price;
        valuationType = PriceValuationType.VALUATION;
        break;
      // Zoopla-specific modes
      case ZooplaPricePerUnitModes.VAL_ESTIMATE:
      case ZooplaPricePerAreaModes.VAL_ESTIMATE:
        unitValue = zoopla?.sale?.currentValue ?? null;
        break;
      case ZooplaPricePerUnitModes.VAL_LAST:
      case ZooplaPricePerAreaModes.VAL_LAST:
        unitValue = zoopla?.sale?.lastValue ?? null;
        valuationType = zoopla?.sale?.lastValueType as PriceValuationType;
        if (!valuationType && unitValue === transaction.price) {
          valuationType = PriceValuationType.SALE;
        }
        break;
      case ZooplaPricePerUnitModes.RENT_ESTIMATE:
      case ZooplaPricePerAreaModes.RENT_ESTIMATE:
        unitValue = zoopla?.rent?.currentValue ?? null;
        isRent = true;
        break;
      case ZooplaPricePerUnitModes.RENT_ASKING:
      case ZooplaPricePerAreaModes.RENT_ASKING:
        unitValue = zoopla?.rent?.lastValue ?? null;
        valuationType = zoopla?.rent?.lastValueType as PriceValuationType;
        isRent = true;
        break;
      default:
        unitValue = transaction.price;
        valuationType = PriceValuationType.SALE;
    }
  }

  const formattedPrice =
    unitValue !== null
      ? formatToCurrency({
          amount: unitValue,
          currency,
          locale,
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        }) + (isRent ? " pcm" : "")
      : "N/A";

  let formattedPerAreaPrice: string | null = null;
  if (floorArea && unitValue !== null) {
    const perAreaAmount = pricePerUnitArea({ price: unitValue, area: floorArea, unitPreference });
    const formattedPpu = formatToCurrency({
      amount: perAreaAmount,
      currency,
      locale,
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    let unit = t(unitPreference === MeasurementSystem.METRIC ? "comparables.sqm.alt" : "comparables.sqft.alt");
    if (isRent) unit += " pcm";
    formattedPerAreaPrice = t("comparables.ppu.slash", {
      price: formattedPpu || "-",
      unit,
    });
  }

  return { formattedPrice, formattedPerAreaPrice, isRent, valuationType };
};

export const useComparablePrices = (
  transactions: EnrichedComparableDTO[],
  locale: Locale,
  currency: Currency,
  filterNAPrices = false
): EnhancedTransaction[] => {
  const { t } = useTranslation();
  const { RHPdisplayBy } = useSnapshot(comparablesStore);
  const hasFullAccess = hasFeature(Feature.zooplaFullAccess);
  const unitPreference = getMeasurementSystemPreference();

  const enhancedTransactions = transactions.map((transaction) => {
    const priceData = calculateComparablePrice(
      transaction,
      locale,
      currency,
      RHPdisplayBy,
      hasFullAccess,
      unitPreference,
      t as (key: string, params?: Record<string, unknown>) => string
    );
    return {
      ...transaction,
      ...priceData,
    };
  });

  // Filter out transactions with N/A prices
  return filterNAPrices
    ? enhancedTransactions.filter((transaction) => transaction.formattedPrice !== "N/A")
    : enhancedTransactions;
};
